import { useCallback, type ReactElement } from 'react'
import QRCode from 'qrcode'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog.js'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  url: string
}

/**
 * Modal containing a QR code for an arbitrary URL. Renders the QR client-side
 * onto a <canvas>; no third-party service, no analytics. The URL is also shown
 * as text so a user can manually copy it as fallback.
 *
 * Uses a CALLBACK REF (not useRef + useEffect) because Radix Dialog mounts
 * its content in a portal — when `open` flips true, useEffect for `open`
 * runs before the portal contents (including the canvas) are committed,
 * so canvasRef.current is null and the QR never renders. The callback ref
 * fires when the canvas is actually attached to the DOM, so the QR draw
 * is correctly synchronized to the canvas's lifecycle.
 */
export const TryOnPhoneDialog = ({ open, onOpenChange, url }: Props): ReactElement => {
  const drawQR = useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas) return
    void QRCode.toCanvas(canvas, url, {
      width: 256,
      margin: 2,
      errorCorrectionLevel: 'M',
    }).catch(err => console.warn('[TryOnPhoneDialog] QR render failed:', err))
  }, [url])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <div className="flex flex-col items-center gap-4 pt-4">
          <DialogTitle className="text-lg font-semibold">Try it on your phone</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground text-center">
            Scan with your phone to continue this demo with the real camera.
          </DialogDescription>
          <canvas ref={drawQR} className="rounded-lg" />
          <code className="text-xs text-muted-foreground select-all break-all px-4 text-center">
            {url}
          </code>
        </div>
      </DialogContent>
    </Dialog>
  )
}
